import { disposeMesh } from 'src/utils/object3DHelpers';
import { ComponentInteractionType, Inputs, PointType, SceneComponent } from './types';

const RADIUS = 0.4;
const WIDTH_SEGMENTS = 32;
const HEIGHT_SEGMENTS = 32;
const DEFAULT_INPUTS = {
  type: PointType.Start,
  colorDefault: '#ea475c',
  colorHover: '#ffffff',
  colorClick: '#ea475c',
  opacityDefault: 0.5,
  opacityHover: 0.4,
  opacityClick: 0.5,
  edgesOpacityDefault: 0.5,
  edgesOpacityHover: 0.7,
  edgesOpacityClick: 0.8,
};

export class PathPointComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private point: any = null;

  private currentOpacity = 0.2;

  private clicked = false;

  inputs: Inputs = {
    ...DEFAULT_INPUTS,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;

    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.currentOpacity = this.inputs.opacityDefault;

    this.makeSphere();
    this.pivot.position.y = RADIUS;
  }

  onDestroy() {
    if (this.point) {
      disposeMesh(this.point);
      this.point = null;
    }
  }

  // Called back when any inputs properties change like localPosition or localRotation
  onInputsUpdated() {
    this.onInit();
  }

  // used to fire it from outside event handler,
  // as internal handler doesn't work if outside handler is defined
  handleHover(event: any) {
    if (!this.clicked) {
      if (event.hover) {
        this.currentOpacity = this.inputs.opacityHover;
      } else {
        this.currentOpacity = this.inputs.opacityDefault;
      }
      if (this.point) {
        (this.point.material as any).opacity = this.currentOpacity;
      }
    }
  }

  handleClick() {
    if (!this.clicked) {
      this.currentOpacity = this.inputs.opacityClick;
      this.clicked = true;
    } else {
      this.currentOpacity = this.inputs.opacityDefault;
      this.clicked = false;
    }
    if (this.point) {
      (this.point.material as any).opacity = this.currentOpacity;
    }
    setTimeout(() => {
      this.clicked = false;
    }, 1000);
  }

  setSelection(color: string) {
    this.inputs.colorDefault = color || this.inputs.colorClick;
    this.inputs.opacityDefault = color ? this.inputs.opacityDefault : this.inputs.opacityClick;
  }

  resetToDefaultColor() {
    this.inputs.colorDefault = DEFAULT_INPUTS.colorClick;
    this.inputs.opacityClick = DEFAULT_INPUTS.opacityClick;
  }

  hide() {
    (this.point.material as any).opacity = 0;
  }

  private makeSphere() {
    const THREE = this.context.three;

    if (this.point) {
      disposeMesh(this.point);
      this.point = null;
    }

    const geometry: any = new THREE.SphereGeometry(RADIUS, WIDTH_SEGMENTS, HEIGHT_SEGMENTS);

    const checkerBoardShader = {
      vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
      fragmentShader: `
    varying vec2 vUv;
    uniform float opacity;
    void main() {
      float checkerSize = 10.0;
      float checker = mod(floor(vUv.x * checkerSize) + floor(vUv.y * checkerSize), 2.0);
      vec3 color = checker > 0.5 ? vec3(1, 1, 1) : vec3(0, 0, 0);
      gl_FragColor = vec4(color, opacity);
    }
  `,
      uniforms: {
        opacity: { value: this.inputs.opacityDefault },
      },
    };

    const material: any =
      this.inputs.type === PointType.Start
        ? new THREE.MeshBasicMaterial({
            color: this.inputs.colorDefault,
            transparent: true,
            opacity: this.inputs.opacityDefault,
          })
        : new THREE.ShaderMaterial({
            uniforms: checkerBoardShader.uniforms,
            vertexShader: checkerBoardShader.vertexShader,
            fragmentShader: checkerBoardShader.fragmentShader,
            transparent: true,
          });

    this.point = new THREE.Mesh(geometry, material);
    this.pivot.add(this.point);
  }
}

export const pathPointComponentType = 'treedis.pathPointComponent';
export const makePathPointComponent = () => new PathPointComponent();
